import { gql } from "@apollo/client"
import styled from "@emotion/styled"
import { mq, Text, theme } from "@pallet-hq/hegel"
import ShareButton from "components/Buttons/ShareButton"
import { InfoWithIcon } from "components/InfoWithIcon"
import moment from "moment"
import { FiBriefcase, FiCalendar, FiMapPin } from "react-icons/fi"
import { useScreenSize } from "utils/hooks/useSize"
import { hasWindow } from "utils/window"
import { PostHeader_post } from "__generated__/PostHeader_post"
import { formatJobTooltipText, formatJobTooltipText2 } from "./utils"

const PostHeaderFragment = gql`
    fragment PostHeader_post on PostType {
        id
        jobTitle
        locations {
            id
            shortLabel
        }
        isOpenToRemote
        experience {
            id
            shortName
        }
        workType {
            id
            name
        }
        salary
        equity
        firstPublishedAt
    }
`

export type PostHeaderProps = {
    post: PostHeader_post
}

const PostHeader = (props: PostHeaderProps) => {
    const currentUrl = hasWindow() ? window.location.href : ""

    const location = formatJobTooltipText2(props.post.locations)
    const isRemote = props.post.isOpenToRemote
    const locationText = location
        ? `${location.text}${isRemote ? " · Remote" : ""}`
        : `${isRemote ? "Remote" : ""}`

    const experiences = formatJobTooltipText(props.post.experience)
    const workType = props.post.workType
    const { isMobile } = useScreenSize()
    return (
        <>
            <PostTitleDiv>
                <PostTitleTextDiv>
                    <Text variant={["headlineSmall", "headlineMedium"]}>
                        {props.post.jobTitle}
                    </Text>
                </PostTitleTextDiv>
                <IconDiv>
                    <ShareButton location={currentUrl} />
                </IconDiv>
            </PostTitleDiv>
            <PostMoreDiv>
                <MoreDiv>
                    {locationText && (
                        <InfoWithIcon
                            icon={FiMapPin}
                            text={locationText}
                            tooltip={location?.tooltip || ""}
                        />
                    )}
                    {experiences && (
                        <InfoWithIcon
                            icon={FiBriefcase}
                            text={`${experiences.text}${
                                workType ? " · " + workType.name : ""
                            }`}
                            tooltip={experiences.tooltip}
                        />
                    )}
                    {props.post.salary && (
                        <InfoWithIcon text={props.post.salary} />
                    )}
                    {props.post.equity && (
                        <>
                            <InfoWithIcon text={props.post.equity} />
                        </>
                    )}
                    {isMobile && (
                        <InfoWithIcon
                            icon={FiCalendar}
                            text={moment(props.post.firstPublishedAt).fromNow()}
                        />
                    )}
                </MoreDiv>
                {!isMobile && (
                    <InfoWithIcon
                        icon={FiCalendar}
                        text={
                            "Posted " +
                            moment(props.post.firstPublishedAt).fromNow()
                        }
                    />
                )}
            </PostMoreDiv>
        </>
    )
}

PostHeader.fragment = PostHeaderFragment

const PostTitleTextDiv = styled.div`
    border: 1px solid transparent;
    padding: ${theme.space.xxsmall} ${theme.space.xsmall};
    margin: 0px calc(-1 * ${theme.space.xsmall});
`

const PostTitleDiv = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${mq({ justifyContent: ["space-between", "unset"] })}
`

const IconDiv = styled.div`
    margin-left: ${theme.space.medium};
    cursor: pointer;
`

const PostMoreDiv = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: ${theme.space.small};
    margin-bottom: ${theme.space.small};
    ${mq({ flexDirection: ["column", "row"] })};
    ${mq({
        padding: [`0 0 ${theme.space.xxsmall}`, `0 0 ${theme.space.small}`],
    })}
    ${mq({ borderBottom: ["unset", `1px solid ${theme.colors.gray200}`] })}
`

const MoreDiv = styled.div`
    display: flex;
    ${mq({ flexDirection: ["column", "row"] })};

    > div:not(:last-child) {
        margin-right: ${theme.space.small};
    }
`

export default PostHeader
